import { functions } from "../firebase";

export type DomainRecord = {
  record: string;
  name: string;
  type: string;
  value: string;
  status: string | null;
  priority: number | null;
};

export type DomainStatus = {
  id: string;
  name: string;
  status: string;
  records: DomainRecord[];
};

export type EmailAutomationStatus = {
  provider: "resend" | null;
  connected: boolean;
  fromEmail: string | null;
  fromName: string | null;
  replyTo: string | null;
  dailyLimit: number | null;
  subscriberCount: number | null;
  unsubscribedCount: number | null;
  suppressedCount: number | null;
  queuedCount: number | null;
  welcomeStepCount: number;
  domain: DomainStatus | null;
  managedDomain: boolean;
  suggestedFromEmail: string | null;
  sendingPaused: boolean;
  pauseReason: string | null;
  pausedAt: string | null;
};

export type WelcomeStep = {
  subject: string;
  body: string;
  delayDays: number;
};

export type WelcomeExperimentVariant = {
  key: string;
  weight: number;
  steps: WelcomeStep[];
};

export type WelcomeExperiment = {
  id: string;
  enabled: boolean;
  hypothesis: string;
  primaryMetric: "unique_open" | "plan_started" | "activation_first_action";
  version: number;
  iteration: number;
  parentExperimentId: string | null;
  startedAt: string;
  variants: WelcomeExperimentVariant[];
};

export const getEmailAutomationStatus = async (
  communityId: string
): Promise<EmailAutomationStatus> => {
  const callable = functions.httpsCallable("getEmailAutomationStatus");
  const result = await callable({ communityId });
  return result.data as EmailAutomationStatus;
};

export const setupEmailAutomation = async (params: {
  communityId: string;
  useTribedDomain?: boolean;
  localPart?: string;
  fromEmail?: string;
  fromName?: string;
  replyTo?: string;
  dailyLimit?: number | null;
  postalAddress?: string;
}): Promise<{ domain: DomainStatus | null }> => {
  const callable = functions.httpsCallable("setupEmailAutomation");
  const result = await callable(params);
  return result.data as { domain: DomainStatus | null };
};

export const verifyEmailDomain = async (
  communityId: string
): Promise<{ domain: DomainStatus }> => {
  const callable = functions.httpsCallable("verifyEmailDomain");
  const result = await callable({ communityId });
  return result.data as { domain: DomainStatus };
};

export type SequenceType = "welcome" | "winback";

export type SequenceSettings = {
  steps: WelcomeStep[];
  enabled: boolean;
  inactiveDays: number;
  cooldownDays: number;
  experiment: WelcomeExperiment | null;
};

export const getEmailSequence = async (
  communityId: string,
  sequenceType: SequenceType
): Promise<SequenceSettings> => {
  const callable = functions.httpsCallable("getWelcomeSequence");
  const result = await callable({ communityId, sequenceType });
  return result.data as SequenceSettings;
};

export const saveEmailSequence = async (params: {
  communityId: string;
  sequenceType: SequenceType;
  steps: WelcomeStep[];
  enabled?: boolean;
  inactiveDays?: number;
  cooldownDays?: number;
  experiment?: WelcomeExperiment | null;
}): Promise<{ ok: true }> => {
  const callable = functions.httpsCallable("saveWelcomeSequence");
  const result = await callable(params);
  return result.data as { ok: true };
};

export type WelcomeExperimentVariantResult = {
  key: string;
  enrolled: number;
  sent: number;
  uniqueOpens: number;
  totalOpens: number;
  uniqueOpenRate: number;
  bounces: number;
  complaints: number;
  unsubscribes: number;
  bounceRate: number;
  complaintRate: number;
  unsubscribeRate: number;
  appOpens: number;
  appOpenRate: number;
  planStarts: number;
  planStartRate: number;
  activations: number;
  activationRate: number;
};

export type WelcomeExperimentResults = {
  communityId: string;
  experimentId: string;
  hypothesis: string;
  primaryMetric: WelcomeExperiment["primaryMetric"];
  iteration: number;
  parentExperimentId: string | null;
  status: "running" | "paused" | "completed";
  startedAt: string | null;
  endedAt: string | null;
  variants: WelcomeExperimentVariantResult[];
};

export const getWelcomeExperimentResults = async (
  communityId: string,
  experimentId?: string
): Promise<WelcomeExperimentResults> => {
  const callable = functions.httpsCallable("getWelcomeExperimentResults");
  const result = await callable({ communityId, experimentId });
  return result.data as WelcomeExperimentResults;
};

export type MiloPolicy = {
  reviewAfterDays: number;
  maxRunDays: number;
  minEnrollmentsPerVariant: number;
  minPrimaryEvents: number;
  confidenceLevel: number;
  maxBounceRate: number;
  maxComplaintRate: number;
  maxUnsubscribeRate: number;
};

export type MiloQueueTask = {
  id: string;
  cycle: string;
  communityId: string;
  communityName: string;
  status:
    | "blocked"
    | "queued"
    | "drafted"
    | "running"
    | "ready_for_review"
    | "measuring"
    | "completed"
    | "rolled_back"
    | "inconclusive"
    | "error";
  blockedReasons?: string[];
  hypothesis?: string;
  primaryMetric?: WelcomeExperiment["primaryMetric"];
  activeExperimentId?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  verdict?: string | null;
  reason?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
};

export type MiloAttention = {
  cycle: string;
  pendingCount: number;
  blockedCount: number;
  statusCounts: Record<string, number>;
  attentionRequired: boolean;
  updatedAt: string;
  lastReminderWeek: string | null;
  lastReminderAt: string | null;
};

export type MiloEmailStatus = {
  agent: {
    id: "milo";
    name: "MILO";
    role: "Monthly Iteration & Lifecycle Optimizer";
    enabled: boolean;
    autoStart: boolean;
    platformAgnostic: boolean;
    protocol: "mcp";
    notificationEmail: string | null;
    notificationsEnabled: boolean;
    policy: MiloPolicy;
    attention: MiloAttention | null;
    lastQueueSyncAt: string | null;
    lastRunAt: string | null;
  };
  communityPendingCount: number;
  currentExperiment: WelcomeExperiment | null;
  latestTask: MiloQueueTask | null;
  recentTasks: MiloQueueTask[];
  sendingPaused: boolean;
  pauseReason: string | null;
  pausedAt: string | null;
};

export const getMiloEmailStatus = async (
  communityId: string
): Promise<MiloEmailStatus> => {
  const callable = functions.httpsCallable("getMiloEmailStatus");
  const result = await callable({ communityId });
  return result.data as MiloEmailStatus;
};

export const resumeMiloEmailSending = async (
  communityId: string
): Promise<{ resumed: true }> => {
  const callable = functions.httpsCallable("resumeMiloEmailSending");
  const result = await callable({ communityId });
  return result.data as { resumed: true };
};

export const sendTestEmail = async (params: {
  communityId: string;
  subject: string;
  body: string;
}): Promise<{ sentTo: string }> => {
  const callable = functions.httpsCallable("sendTestEmail");
  const result = await callable(params);
  return result.data as { sentTo: string };
};

export const sendEmailCampaignNow = async (params: {
  communityId: string;
  subject: string;
  body: string;
}): Promise<{ campaignId: string; recipients: number }> => {
  const callable = functions.httpsCallable("sendEmailCampaignNow");
  const result = await callable(params);
  return result.data as { campaignId: string; recipients: number };
};

export const sendDraftEmailCampaign = async (
  communityId: string,
  campaignId: string
): Promise<{ campaignId: string; recipients: number }> => {
  const callable = functions.httpsCallable("sendDraftEmailCampaign");
  const result = await callable({ communityId, campaignId });
  return result.data as { campaignId: string; recipients: number };
};

export type ImportEmailSubscriber = { email: string; name?: string };

export type ImportEmailSubscribersResult = {
  imported: number;
  existing: number;
  invalid: number;
  total: number;
};

export const importEmailSubscribers = async (
  communityId: string,
  subscribers: ImportEmailSubscriber[]
): Promise<ImportEmailSubscribersResult> => {
  const callable = functions.httpsCallable("importEmailSubscribers");
  const result = await callable({ communityId, subscribers });
  return result.data as ImportEmailSubscribersResult;
};
